/**
 * The ONE rule for which fields of an authored journey/mission page are
 * translatable. Used by the overlay merge at runtime and by
 * frontend/scripts/extract-journey-strings.js, so they can never disagree.
 *
 * A string is translatable when it is displayed to the student:
 *   1. its property name is in TRANSLATABLE_STRING_KEYS, or
 *   2. it is a direct element of an array whose property name is in
 *      TRANSLATABLE_STRING_ARRAY_KEYS (bullet / numbered items, suggested
 *      Saras questions), or
 *   3. it is a value of a map whose property name is in TRANSLATABLE_MAP_KEYS
 *      (display labels for saved values / fields).
 *
 * NEVER translated (identifiers, wiring, metadata, AI-facing text): id, type,
 * step_id, source_step_id, save_as, field, condition, correct_answer,
 * correct_order, correct_category, value, tone, phase, instructional_mode,
 * grouping, teacher_cue, path, poster, scene_name, base_board, source_type,
 * asset_ref/provenance/content_context data, hidden_context, reveal_policy.
 * (hidden_context and reveal_policy steer the AI tutor and stay English.)
 *
 * Overlay paths are dotted with numeric array indices, relative to the page
 * object, e.g. "title", "blocks.2.options.1.label", "blocks.0.reading.content.3.text".
 */
export const TRANSLATABLE_STRING_KEYS = new Set([
  "title",
  "subtitle",
  "story",
  "eyebrow",
  "prompt",
  "body",
  "text",
  "label",
  "caption",
  "alt",
  "placeholder",
  "mission",
  "help",
  "student_cue",
  "tab_label",
  "reveal_text",
  "empty_text",
  "correct_feedback",
  "incorrect_feedback",
  "retry_feedback",
  "complete_title",
  "incomplete_title",
  "complete_prompt",
  "incomplete_prompt",
]);

export const TRANSLATABLE_STRING_ARRAY_KEYS = new Set([
  "items",
  "suggested_questions",
]);

export const TRANSLATABLE_MAP_KEYS = new Set(["value_labels", "field_labels"]);

/**
 * Proper nouns of the Conductive Dough story (places, characters, roles, kit
 * name). The story video says them in English, so every translated overlay must
 * keep them verbatim in Latin script; only the grammar around them is localised.
 * Overlay test: __tests__/journeyOverlayFiles.test.js.
 */
export const CONDUCTIVE_DOUGH_PROPER_NOUNS = [
  "Festival Keeper Luma",
  "Master Electro-Sculptor",
  "Master Sculptor",
  "Circuit Sculptor",
  "Glow City",
  "Festival of Light",
  "Beacon Tower",
  "Luma",
  "Squishy Circuits",
];

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isNonEmptyString = (value) =>
  typeof value === "string" && value.trim() !== "";

/**
 * Walks `node`; calls visit(path, string) for each translatable string.
 * `key` is the property name `node` sits under; `parentKey` is the key of the
 * array/map container (for array elements and map values).
 */
const walk = (node, path, key, containerKey, visit) => {
  if (typeof node === "string") {
    const translatable =
      TRANSLATABLE_STRING_KEYS.has(key) ||
      TRANSLATABLE_STRING_ARRAY_KEYS.has(containerKey) ||
      TRANSLATABLE_MAP_KEYS.has(containerKey);
    if (translatable && isNonEmptyString(node)) visit(path, node);
    return;
  }

  if (Array.isArray(node)) {
    node.forEach((item, index) =>
      walk(item, path ? `${path}.${index}` : String(index), key, key, visit)
    );
    return;
  }

  if (isPlainObject(node)) {
    Object.keys(node).forEach((childKey) =>
      walk(
        node[childKey],
        path ? `${path}.${childKey}` : childKey,
        childKey,
        TRANSLATABLE_MAP_KEYS.has(key) ? key : "",
        visit
      )
    );
  }
};

/** Returns { "<path>": "<english string>" } for every translatable field. */
export const collectTranslatableStrings = (page) => {
  const result = {};
  walk(page, "", "", "", (path, value) => {
    result[path] = value;
  });
  return result;
};
